
class CartSubscribeSaveComponent extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('.cart-subscribe-save__checkbox');
    this.isUpdating = false;
    this.checkTimeout = null;
    this.init();
  }

  init() {
    if (!this.checkbox) {
      console.error('❌ Checkbox not found');
      return;
    }
    
    console.log('✅ Component initialized');
    this.checkbox.addEventListener('change', this.handleToggle.bind(this));
    
    // Listen for cart updates
    this.listenForCartUpdates();
    
    // Watch for DOM changes in cart
    this.observeCartChanges();
    
    setTimeout(() => {
      this.checkInitialState();
    }, 500);
  }

  observeCartChanges() {
    // Watch for changes in cart items
    const cartItemsComponent = document.querySelector('cart-items-component');
    
    if (cartItemsComponent) {
      const observer = new MutationObserver(() => {
        if (!this.isUpdating) {
          console.log('👀 Cart DOM changed - rechecking state');
          this.debouncedCheckState();
        }
      });

      observer.observe(cartItemsComponent, {
        childList: true,
        subtree: true
      });

      console.log('✅ Cart observer attached');
    }
  }

  debouncedCheckState() {
    // Clear existing timeout
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
    }

    // Set new timeout
    this.checkTimeout = setTimeout(() => {
      this.checkInitialState();
    }, 500);
  }

  listenForCartUpdates() {
    // Listen for all possible cart events
    const events = [
      'cart:updated',
      'cart:refresh', 
      'cart:item-added',
      'cart:change',
      'cart-drawer:open',
      'cart-drawer:refresh'
    ];

    events.forEach(eventName => {
      document.addEventListener(eventName, () => {
        if (!this.isUpdating) {
          console.log(`🔔 Event: ${eventName} - rechecking state`);
          this.debouncedCheckState();
        }
      });
    });

    // Also listen on document.documentElement
    document.documentElement.addEventListener('cart:updated', () => {
      if (!this.isUpdating) {
        console.log('🔔 Document cart:updated - rechecking state');
        this.debouncedCheckState();
      }
    });

    console.log('✅ Cart event listeners attached');
  }

  async checkInitialState() {
    // Don't check while updating
    if (this.isUpdating) {
      console.log('⏸️ Skipping state check - update in progress');
      return;
    }

    console.log('🔍 Checking cart state...');
    
    try {
      const cart = await this.getCart();
      console.log('📦 Cart items:', cart.items.length);
      
      if (cart.items.length === 0) {
        console.log('⚠️ Cart is empty - unchecking');
        this.checkbox.checked = false;
        return;
      }

      let itemsWithPlans = 0;
      let subscriptionItems = 0;

      for (const item of cart.items) {
        const hasPlans = await this.hasSellingPlans(item.handle, item.variant_id);
        
        if (hasPlans) {
          itemsWithPlans++;
          console.log(`  ${item.product_title}: has plans`);
          
          if (item.selling_plan_allocation) {
            subscriptionItems++;
            console.log(`    ✅ Is subscription`);
          } else {
            console.log(`    ❌ Not subscription`);
          }
        }
      }

      console.log(`📊 Summary: ${subscriptionItems}/${itemsWithPlans} items are subscriptions`);

      // Checkbox should be checked if ALL items with plans are subscriptions
      const shouldBeChecked = itemsWithPlans > 0 && subscriptionItems === itemsWithPlans;
      
      console.log(`🎯 Setting checkbox to: ${shouldBeChecked}`);
      
      if (this.checkbox.checked !== shouldBeChecked) {
        this.checkbox.checked = shouldBeChecked;
        console.log('✅ Checkbox state updated');
      } else {
        console.log('ℹ️ Checkbox already in correct state');
      }
      
    } catch (error) {
      console.error('❌ Error checking initial state:', error);
    }
  }

  async hasSellingPlans(handle, variantId) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return false;
      
      const product = await response.json();
      const variant = product.variants.find(v => v.id === variantId);
      
      return variant?.selling_plan_allocations?.length > 0;
    } catch (error) {
      return false;
    }
  }

  async handleToggle(event) {
    if (this.isUpdating) {
      event.preventDefault();
      return;
    }

    const isSubscription = event.target.checked;
    this.isUpdating = true;
    this.checkbox.disabled = true;

    console.log('\n🔄 Starting update to:', isSubscription ? 'SUBSCRIPTION' : 'ONE-TIME');

    try {
      const cart = await this.getCart();
      console.log('📦 Current cart:', cart);

      if (cart.items.length === 0) {
        console.log('⚠️ Cart is empty');
        this.isUpdating = false;
        this.checkbox.disabled = false;
        return;
      }

      const itemsToUpdate = [];

      for (const item of cart.items) {
        console.log(`\n🔍 Checking item: ${item.product_title} (${item.variant_id})`);
        
        const hasPlans = await this.hasSellingPlans(item.handle, item.variant_id);
        console.log('  Has selling plans:', hasPlans);
        
        if (!hasPlans) {
          console.log('  ⏭️ Skipping - no selling plans available');
          continue;
        }

        const currentlySubscription = item.selling_plan_allocation !== null && item.selling_plan_allocation !== undefined;
        console.log('  Currently subscription:', currentlySubscription);
        console.log('  Target subscription:', isSubscription);
        console.log('  Needs update:', currentlySubscription !== isSubscription);

        if (currentlySubscription !== isSubscription) {
          let sellingPlanId = null;
          if (isSubscription) {
            const response = await fetch(`/products/${item.handle}.js`);
            const product = await response.json();
            const variant = product.variants.find(v => v.id === item.variant_id);
            sellingPlanId = variant.selling_plan_allocations[0].selling_plan_id;
            console.log('  Selling plan ID:', sellingPlanId);
          }

          itemsToUpdate.push({
            key: item.key,
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {},
            selling_plan_id: sellingPlanId
          });
          
          console.log('  ✅ Added to update queue');
        } else {
          console.log('  ⏭️ Already in correct state - no update needed');
        }
      }

      console.log('\n📋 Total items to update:', itemsToUpdate.length);

      if (itemsToUpdate.length === 0) {
        console.log('✅ No updates needed - all items already in correct state');
        this.isUpdating = false;
        this.checkbox.disabled = false;
        return;
      }

      console.log('\n🗑️ Step 1: Removing old items...');
      const updates = {};
      itemsToUpdate.forEach(item => {
        updates[item.key] = 0;
        console.log(`  Removing: ${item.key}`);
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      console.log('✅ Items removed');
      await new Promise(resolve => setTimeout(resolve, 400));

      console.log('\n➕ Step 2: Adding items back...');
      for (const item of itemsToUpdate) {
        const payload = {
          items: [{
            id: item.variant_id,
            quantity: item.quantity
          }]
        };

        if (item.selling_plan_id) {
          payload.items[0].selling_plan = item.selling_plan_id;
          console.log(`  Adding variant ${item.variant_id} with selling plan: ${item.selling_plan_id}`);
        } else {
          console.log(`  Adding variant ${item.variant_id} as one-time purchase`);
        }

        if (Object.keys(item.properties).length > 0) {
          payload.items[0].properties = item.properties;
        }

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        console.log('  ✅ Item added');
        await new Promise(resolve => setTimeout(resolve, 250));
      }

      console.log('\n✅ All items updated successfully!');
      
      await this.refreshCartDrawer();

    } catch (error) {
      console.error('\n❌ Error updating cart:', error);
      this.checkbox.checked = !isSubscription;
      alert('Unable to update subscription. Please try again.');
    } finally {
      this.isUpdating = false;
      this.checkbox.disabled = false;
      
      // Recheck state after update completes
      setTimeout(() => {
        this.checkInitialState();
      }, 800);
    }
  }

  async refreshCartDrawer() {
    console.log('🔄 Refreshing cart drawer...');
    
    try {
      const cartItemsComponent = document.querySelector('cart-items-component');
      
      if (!cartItemsComponent) {
        console.log('⚠️ cart-items-component not found');
        return;
      }

      const sectionId = cartItemsComponent.dataset.sectionId || 'cart-drawer';
      const url = `${window.location.pathname}?section_id=${sectionId}`;
      
      const response = await fetch(url);
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newItems = doc.querySelector('.cart-drawer__items');
      const currentItems = cartItemsComponent.querySelector('.cart-drawer__items');
      
      if (newItems && currentItems) {
        currentItems.innerHTML = newItems.innerHTML;
        console.log('✅ Cart items updated');
      }
      
      const newSummary = doc.querySelector('.cart-drawer__summary');
      const currentSummary = document.querySelector('.cart-drawer__summary');
      
      if (newSummary && currentSummary) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newSummary.innerHTML;
        
        tempDiv.querySelectorAll('cart-subscribe-save-component').forEach(comp => comp.remove());
        
        const newTotals = tempDiv.querySelector('.cart__summary-totals');
        const currentTotals = currentSummary.querySelector('.cart__summary-totals');
        
        if (newTotals && currentTotals) {
          currentTotals.innerHTML = newTotals.innerHTML;
          console.log('✅ Cart totals updated');
        }
      }
      
      const cartBubbles = document.querySelectorAll('.cart-bubble__text-count');
      const newBubble = doc.querySelector('.cart-bubble__text-count');
      
      if (newBubble) {
        cartBubbles.forEach(bubble => {
          bubble.textContent = newBubble.textContent;
        });
        console.log('✅ Cart count updated');
      }
      
      const cart = await this.getCart();
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));
      
      console.log('✅ Cart drawer refresh complete');
      
    } catch (error) {
      console.error('❌ Error refreshing cart drawer:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }
}

customElements.define('cart-subscribe-save-component', CartSubscribeSaveComponent);



class CartItemSubscriptionToggle extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('.cart-item-sub__checkbox');
    this.itemKey = this.dataset.itemKey;
    this.variantId = this.dataset.variantId;
    this.productHandle = this.dataset.productHandle;
    this.hasSellingPlan = this.dataset.hasSellingPlan === 'true';
    this.isUpdating = false;
    
    console.log('🎯 Cart item subscription toggle initialized for:', this.itemKey);
    this.init();
  }

  init() {
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.handleToggle.bind(this));
      console.log(`✅ Checkbox initialized - Currently: ${this.checkbox.checked ? 'SUBSCRIPTION' : 'ONE-TIME'}`);
    }
  }

  async handleToggle(event) {
    if (this.isUpdating) {
      console.log('⏸️ Update already in progress, skipping...');
      return;
    }

    const isSubscription = event.target.checked;
    console.log(`\n🔄 Toggling item ${this.itemKey} to: ${isSubscription ? 'SUBSCRIPTION' : 'ONE-TIME'}`);
    
    this.isUpdating = true;
    
    try {
      // Get current cart item details
      const cart = await this.getCart();
      const cartItem = cart.items.find(item => item.key === this.itemKey);
      
      if (!cartItem) {
        throw new Error('Cart item not found');
      }
      
      console.log('📦 Current cart item:', cartItem);
      
      // Update the cart item with or without selling plan
      const updated = await this.updateCartItem(cartItem, isSubscription);
      
      if (updated) {
        console.log('✅ Item updated successfully');
        
        // Refresh the cart drawer
        await this.refreshCartDrawer();
        console.log('✅ Cart drawer refreshed');
      } else {
        // Revert checkbox if update failed
        this.checkbox.checked = !isSubscription;
        this.showError('Unable to update subscription for this item.');
      }
      
    } catch (error) {
      console.error('❌ Error updating item:', error);
      // Revert checkbox state on error
      this.checkbox.checked = !isSubscription;
      this.showError('Unable to update subscription. Please try again.');
    } finally {
      this.isUpdating = false;
    }
  }

  async updateCartItem(item, isSubscription) {
    try {
      console.log(`   🔍 Fetching product data for: ${this.productHandle}`);
      
      // Get the product data to find selling plan
      const productResponse = await fetch(`/products/${this.productHandle}.js`);
      
      if (!productResponse.ok) {
        console.error(`   ❌ Failed to fetch product: ${productResponse.status}`);
        return false;
      }
      
      const product = await productResponse.json();
      console.log(`   ✅ Product data fetched:`, product.title);
      
      // Find the variant
      const variant = product.variants.find(v => v.id === parseInt(this.variantId));
      
      if (!variant) {
        console.error(`   ❌ Variant not found for ID: ${this.variantId}`);
        return false;
      }
      
      console.log(`   ✅ Variant found:`, variant.title);
      console.log(`   📋 Selling plan allocations:`, variant.selling_plan_allocations);
      
      let sellingPlanId = null;
      
      if (isSubscription) {
        if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
          sellingPlanId = variant.selling_plan_allocations[0].selling_plan_id;
          console.log(`   ✅ Selling plan ID found: ${sellingPlanId}`);
        } else {
          console.log(`   ⚠️ No selling plans available for this variant`);
          return false;
        }
      } else {
        sellingPlanId = null;
        console.log(`   ℹ️ Removing selling plan (switching to one-time purchase)`);
      }
      
      const updatePayload = {
        id: item.key,
        quantity: item.quantity,
        selling_plan: sellingPlanId
      };
      
      if (item.properties && Object.keys(item.properties).length > 0) {
        updatePayload.properties = item.properties;
      }
      
      console.log(`   📤 Sending update request:`, updatePayload);
      
      const updateResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (!updateResponse.ok) {
        console.error(`   ❌ Update failed: ${updateResponse.status}`);
        const errorText = await updateResponse.text();
        console.error(`   Error response:`, errorText);
        return false;
      }
      
      const updatedItem = await updateResponse.json();
      console.log(`   ✅ Update successful:`, updatedItem);
      
      return true;
      
    } catch (error) {
      console.error(`   ❌ Error updating item:`, error);
      return false;
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return cart;
  }

  async refreshCartDrawer() {
    console.log('🔄 Refreshing cart drawer...');
    
    try {
      const cartItemsComponent = document.querySelector('cart-items-component');
      
      if (!cartItemsComponent) {
        console.error('   ❌ cart-items-component not found');
        return;
      }
      
      // Get section ID
      const sectionId = cartItemsComponent.dataset.sectionId || 'cart-drawer';
      
      // Fetch updated sections
      const sectionsToFetch = [sectionId, 'cart-icon-bubble'];
      const url = `${window.location.pathname}?sections=${sectionsToFetch.join(',')}`;
      
      console.log('   📡 Fetching sections from:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.status}`);
      }
      
      const sections = await response.json();
      console.log('   ✅ Sections received');
      
      // Update cart drawer section
      if (sections[sectionId]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections[sectionId], 'text/html');
        
        // Update cart items
        const newItemsContent = doc.querySelector('.cart-drawer__items');
        const currentItemsContent = cartItemsComponent.querySelector('.cart-drawer__items');
        
        if (newItemsContent && currentItemsContent) {
          currentItemsContent.innerHTML = newItemsContent.innerHTML;
          console.log('   ✅ Cart items updated');
        }
        
        // Update summary
        const newSummaryContent = doc.querySelector('.cart-drawer__summary');
        const currentSummaryContent = cartItemsComponent.querySelector('.cart-drawer__summary');
        
        if (newSummaryContent && currentSummaryContent) {
          currentSummaryContent.innerHTML = newSummaryContent.innerHTML;
          console.log('   ✅ Cart summary updated');
        }
        
        // Update header
        const newHeader = doc.querySelector('.cart-drawer__header');
        const currentHeader = cartItemsComponent.querySelector('.cart-drawer__header');
        
        if (newHeader && currentHeader) {
          currentHeader.innerHTML = newHeader.innerHTML;
          console.log('   ✅ Cart header updated');
        }
        
        // Update dialog classes
        const newDialog = doc.querySelector('.cart-drawer__dialog');
        const currentDialog = document.querySelector('.cart-drawer__dialog');
        if (newDialog && currentDialog) {
          currentDialog.className = newDialog.className;
        }
      }
      
      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        
        const newBubble = doc.querySelector('.cart-bubble__text-count');
        if (newBubble) {
          const currentBubbles = document.querySelectorAll('.cart-bubble__text-count');
          currentBubbles.forEach(bubble => {
            bubble.textContent = newBubble.textContent;
          });
          console.log('   ✅ Cart count updated');
        }
      }
      
      // Get fresh cart data and dispatch events
      const cart = await this.getCart();
      
      const cartUpdateEvent = new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      });
      
      document.dispatchEvent(cartUpdateEvent);
      document.documentElement.dispatchEvent(cartUpdateEvent);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      
      console.log('   ✅ Cart events dispatched');
      
    } catch (error) {
      console.error('   ❌ Error refreshing cart:', error);
    }
  }

  showError(message) {
    console.error('🚨 ERROR:', message);
    // alert(message);
  }
}

if (!customElements.get('cart-item-subscription-toggle')) {
  customElements.define('cart-item-subscription-toggle', CartItemSubscriptionToggle);
  // console.log('✅ cart-item-subscription-toggle custom element defined');
}








// class CaliforniaProp65Warning {
//   constructor(options = {}) {
//     this.options = {
//       geolocationAPI: options.geolocationAPI || 'https://ipapi.co/json/',
//       // Horizon theme specific selectors based on actual structure
//       cartDrawerSelector: options.cartDrawerSelector || 'cart-drawer-component',
//       cartDrawerDialogSelector: options.cartDrawerDialogSelector || '.cart-drawer__dialog',
//       cartDrawerInnerSelector: options.cartDrawerInnerSelector || '.cart-drawer__inner',
//       cartSummarySelector: options.cartSummarySelector || '.cart-drawer__summary',
//       targetSelector: options.targetSelector || '.cart__ctas',
//       cartTriggerSelector: options.cartTriggerSelector || 'cart-drawer-component button[on\\:click*="/open"]',
//       disclaimerClass: options.disclaimerClass || 'california-disclaimer',
//       cacheKey: options.cacheKey || 'ca_visitor_state',
//       locationKey: options.locationKey || 'ca_visitor_location',
//       cacheDuration: options.cacheDuration || 3600000, // 1 hour
//       checkDelay: options.checkDelay || 500,
//       // VPN/location change detection
//       enableLocationChangeDetection: options.enableLocationChangeDetection !== false
//     };
    
//     this.isCaliforniaVisitor = null;
//     this.isChecking = false;
//     this.cartDrawerElement = null;
//     this.dialogElement = null;
//     this.currentLocation = null;
    
//     this.init();
//   }

//   /**
//    * Initialize the warning system
//    */
//   init() {
//     if (document.readyState === 'loading') {
//       document.addEventListener('DOMContentLoaded', () => this.setup());
//     } else {
//       this.setup();
//     }
//   }

//   /**
//    * Setup event listeners and initial check
//    */
//   setup() {
//     // Find cart drawer elements
//     this.cartDrawerElement = document.querySelector(this.options.cartDrawerSelector);
//     this.dialogElement = document.querySelector(this.options.cartDrawerDialogSelector);
    
//     console.log('CA Prop 65: Initialized', {
//       cartDrawer: !!this.cartDrawerElement,
//       dialog: !!this.dialogElement
//     });
    
//     // Check for location changes on every page load
//     if (this.options.enableLocationChangeDetection) {
//       this.detectLocationChange();
//     }
    
//     // Attach all event listeners
//     this.attachHorizonCartListeners();
//     this.attachCartTriggerListeners();
//     this.attachMutationObserver();
    
//     // Check immediately if cart is already open
//     if (this.isCartDrawerOpen()) {
//       this.checkAndShowWarning();
//     }
//   }

//   /**
//    * Check if cart drawer is currently open
//    * @returns {boolean}
//    */
//   isCartDrawerOpen() {
//     if (!this.dialogElement) {
//       this.dialogElement = document.querySelector(this.options.cartDrawerDialogSelector);
//     }
//     return this.dialogElement?.hasAttribute('open') || false;
//   }

//   /**
//    * Get cached visitor state
//    * @returns {boolean|null}
//    */
//   getCachedState() {
//     try {
//       const cached = sessionStorage.getItem(this.options.cacheKey);
//       if (!cached) return null;

//       const data = JSON.parse(cached);
//       const isExpired = (Date.now() - data.timestamp) > this.options.cacheDuration;
      
//       if (isExpired) {
//         sessionStorage.removeItem(this.options.cacheKey);
//         sessionStorage.removeItem(this.options.locationKey);
//         return null;
//       }

//       return data.isCA;
//     } catch (error) {
//       console.error('CA Prop 65: Error reading cache', error);
//       return null;
//     }
//   }

//   /**
//    * Cache visitor state
//    * @param {boolean} isCA
//    */
//   cacheState(isCA) {
//     try {
//       const data = {
//         isCA: isCA,
//         timestamp: Date.now()
//       };
//       sessionStorage.setItem(this.options.cacheKey, JSON.stringify(data));
//     } catch (error) {
//       console.error('CA Prop 65: Error caching state', error);
//     }
//   }

//   /**
//    * Get cached location data
//    * @returns {Object|null}
//    */
//   getCachedLocation() {
//     try {
//       const cached = sessionStorage.getItem(this.options.locationKey);
//       if (!cached) return null;
//       return JSON.parse(cached);
//     } catch (error) {
//       console.error('CA Prop 65: Error reading location cache', error);
//       return null;
//     }
//   }

//   /**
//    * Cache location data
//    * @param {Object} locationData
//    */
//   cacheLocation(locationData) {
//     try {
//       const data = {
//         ip: locationData.ip,
//         country: locationData.country_code,
//         region: locationData.region_code,
//         city: locationData.city,
//         timestamp: Date.now()
//       };
//       sessionStorage.setItem(this.options.locationKey, JSON.stringify(data));
//       this.currentLocation = data;
//     } catch (error) {
//       console.error('CA Prop 65: Error caching location', error);
//     }
//   }

//   /**
//    * Detect if location has changed (VPN detection)
//    * Compares current IP/location with cached data
//    */
//   async detectLocationChange() {
//     const cachedLocation = this.getCachedLocation();
    
//     if (!cachedLocation) {
//       console.log('CA Prop 65: No cached location, first visit');
//       return;
//     }

//     try {
//       // Fetch current location
//       const response = await fetch(this.options.geolocationAPI);
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
      
//       const currentData = await response.json();
      
//       // Compare IP addresses and location data
//       const ipChanged = cachedLocation.ip !== currentData.ip;
//       const countryChanged = cachedLocation.country !== currentData.country_code;
//       const regionChanged = cachedLocation.region !== currentData.region_code;
//       const cityChanged = cachedLocation.city !== currentData.city;
      
//       console.log('CA Prop 65: Location comparison', {
//         cached: cachedLocation,
//         current: {
//           ip: currentData.ip,
//           country: currentData.country_code,
//           region: currentData.region_code,
//           city: currentData.city
//         },
//         changes: { ipChanged, countryChanged, regionChanged, cityChanged }
//       });

//       // If any significant location data changed, clear cache
//       if (ipChanged || countryChanged || regionChanged) {
//         console.warn('CA Prop 65: 🚨 Location change detected! Clearing cache...');
//         this.clearAllCache();
        
//         // Cache new location
//         this.cacheLocation(currentData);
        
//         // Remove any existing disclaimer
//         this.removeDisclaimer();
        
//         // Force re-check
//         this.isCaliforniaVisitor = null;
//       } else {
//         console.log('CA Prop 65: ✅ Location unchanged');
//       }
      
//     } catch (error) {
//       console.error('CA Prop 65: Error detecting location change', error);
//     }
//   }

//   /**
//    * Clear all cached data
//    */
//   clearAllCache() {
//     try {
//       sessionStorage.removeItem(this.options.cacheKey);
//       sessionStorage.removeItem(this.options.locationKey);
//       this.isCaliforniaVisitor = null;
//       this.currentLocation = null;
//       console.log('CA Prop 65: All cache cleared');
//     } catch (error) {
//       console.error('CA Prop 65: Error clearing cache', error);
//     }
//   }

//   /**
//    * Check if visitor is from California using IP geolocation
//    * @returns {Promise<boolean>}
//    */
//   async checkCaliforniaVisitor() {
//     // Return cached result if available
//     const cached = this.getCachedState();
//     if (cached !== null) {
//       console.log('CA Prop 65: Using cached state ->', cached);
//       return cached;
//     }

//     // Prevent multiple simultaneous checks
//     if (this.isChecking) {
//       return this.isCaliforniaVisitor || false;
//     }

//     this.isChecking = true;

//     try {
//       const response = await fetch(this.options.geolocationAPI);
      
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
      
//       const data = await response.json();
//       console.log('CA Prop 65: Geolocation data ->', {
//         ip: data.ip,
//         region_code: data.region_code,
//         region: data.region,
//         city: data.city,
//         country: data.country_code
//       });
      
//       const isCA = data.region_code === 'CA' || data.region === 'California';
      
//       // Cache the result
//       this.cacheState(isCA);
      
//       // Cache location data for change detection
//       this.cacheLocation(data);
      
//       this.isCaliforniaVisitor = isCA;
      
//       console.log('CA Prop 65: Is California visitor?', isCA);
      
//       return isCA;
//     } catch (error) {
//       console.error('CA Prop 65: Geolocation error', error);
//       return false;
//     } finally {
//       this.isChecking = false;
//     }
//   }

//   /**
//    * Check if disclaimer already exists in the DOM
//    * @returns {boolean}
//    */
//   disclaimerExists() {
//     return document.querySelector(`.${this.options.disclaimerClass}`) !== null;
//   }

//   /**
//    * Create and return the disclaimer HTML element
//    * @returns {HTMLElement}
//    */
//   createDisclaimerElement() {
//     const disclaimer = document.createElement('div');
//     disclaimer.className = this.options.disclaimerClass;
//     disclaimer.setAttribute('data-ca-prop65', 'true');
    
//     disclaimer.innerHTML = `
//       <div class="california-disclaimer__content" style="background: #fff3cd; border: 1px solid #ffc107; padding: 7px; margin:0; border-radius: 4px;">
//         <strong style="display: block; margin-bottom: 2px; color: #856404; font-size: 12px;">⚠️ CA Prop 65 Warning</strong>
//         <p style="margin: 0; font-size: 10px; line-height: 1.5; color: #856404;">
//           One or more of these products may expose you to lead, which is known to the State of California to cause cancer or reproductive harm. For more information, visit <a href="https://www.p65warnings.ca.gov" target="_blank" rel="noopener" style="color: #856404; text-decoration: underline;">P65Warnings.ca.gov</a>.
//         </p>
//       </div>
//     `;
    
//     return disclaimer;
//   }

//   /**
//    * Show the disclaimer in the cart drawer (before .cart__ctas)
//    */
//   showDisclaimer() {
//     // Don't add if already exists
//     if (this.disclaimerExists()) {
//       console.log('CA Prop 65: Disclaimer already exists, skipping');
//       return;
//     }

//     // Find the cart drawer summary section
//     const cartSummary = document.querySelector(this.options.cartSummarySelector);
    
//     if (!cartSummary) {
//       console.warn('CA Prop 65: Cart summary not found');
//       return;
//     }

//     // Find .cart__ctas within the summary
//     const targetElement = cartSummary.querySelector(this.options.targetSelector);
    
//     if (!targetElement) {
//       console.warn('CA Prop 65: Target element (.cart__ctas) not found');
//       return;
//     }

//     const disclaimer = this.createDisclaimerElement();
    
//     // Insert before .cart__ctas
//     targetElement.parentNode.insertBefore(disclaimer, targetElement);
    
//     console.log('CA Prop 65: ✅ Warning displayed successfully');
//   }

//   /**
//    * Remove the disclaimer from the DOM
//    */
//   removeDisclaimer() {
//     const disclaimer = document.querySelector(`.${this.options.disclaimerClass}`);
//     if (disclaimer) {
//       disclaimer.remove();
//       console.log('CA Prop 65: Disclaimer removed');
//     }
//   }

//   /**
//    * Main check and show logic
//    */
//   async checkAndShowWarning() {
//     // Only check if cart drawer is open
//     if (!this.isCartDrawerOpen()) {
//       console.log('CA Prop 65: Cart drawer not open, skipping');
//       return;
//     }

//     const isCA = await this.checkCaliforniaVisitor();
    
//     if (isCA) {
//       // Delay to ensure cart drawer is fully rendered
//       setTimeout(() => this.showDisclaimer(), this.options.checkDelay);
//     } else {
//       // If not CA, make sure disclaimer is removed
//       this.removeDisclaimer();
//     }
//   }

//   /**
//    * Attach listeners specific to Horizon theme cart drawer
//    */
//   attachHorizonCartListeners() {
//     // Listen for cart updates via fetch/XHR
//     const originalFetch = window.fetch;
//     window.fetch = async (...args) => {
//       const response = await originalFetch(...args);
//       const url = args[0];
      
//       // Check if it's a cart-related request
//       if (typeof url === 'string' && (url.includes('/cart') || url.includes('cart.js'))) {
//         console.log('CA Prop 65: Cart fetch detected');
//         setTimeout(() => {
//           if (this.isCartDrawerOpen()) {
//             this.removeDisclaimer();
//             this.checkAndShowWarning();
//           }
//         }, this.options.checkDelay);
//       }
      
//       return response;
//     };

//     // Listen for Shopify section loads
//     document.addEventListener('shopify:section:load', (event) => {
//       if (event.detail?.sectionId?.includes('cart') || event.detail?.sectionId?.includes('header')) {
//         console.log('CA Prop 65: Cart section loaded');
//         setTimeout(() => this.checkAndShowWarning(), this.options.checkDelay);
//       }
//     });
//   }

//   /**
//    * Attach listeners to cart trigger buttons
//    */
//   attachCartTriggerListeners() {
//     // Use event delegation for cart open buttons
//     document.addEventListener('click', (event) => {
//       const trigger = event.target.closest('cart-drawer-component button[on\\:click*="/open"]') ||
//                      event.target.closest('.header__icon--cart') ||
//                      event.target.closest('cart-icon') ||
//                      event.target.closest('[aria-label*="cart"]');
      
//       if (trigger) {
//         console.log('CA Prop 65: Cart trigger clicked');
//         setTimeout(() => this.checkAndShowWarning(), this.options.checkDelay);
//       }
//     });
//   }

//   /**
//    * Attach MutationObserver to detect cart drawer dialog open/close
//    */
//   attachMutationObserver() {
//     if (!this.dialogElement) {
//       this.dialogElement = document.querySelector(this.options.cartDrawerDialogSelector);
//     }

//     if (!this.dialogElement) {
//       console.warn('CA Prop 65: Dialog element not found, retrying in 1s...');
//       setTimeout(() => this.attachMutationObserver(), 1000);
//       return;
//     }

//     // Watch for 'open' attribute changes on dialog
//     const observer = new MutationObserver((mutations) => {
//       mutations.forEach((mutation) => {
//         if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
//           const isOpen = this.dialogElement.hasAttribute('open');
          
//           console.log('CA Prop 65: Dialog open state changed ->', isOpen);
          
//           if (isOpen) {
//             setTimeout(() => this.checkAndShowWarning(), this.options.checkDelay);
//           } else {
//             // Remove disclaimer when cart closes
//             this.removeDisclaimer();
//           }
//         }
//       });
//     });

//     observer.observe(this.dialogElement, {
//       attributes: true,
//       attributeFilter: ['open']
//     });

//     console.log('CA Prop 65: ✅ MutationObserver attached to dialog');
//   }

//   /**
//    * Force refresh - clear cache and re-check
//    */
//   refresh() {
//     console.log('CA Prop 65: Refreshing...');
//     this.clearAllCache();
//     this.removeDisclaimer();
//     this.checkAndShowWarning();
//   }

//   /**
//    * Destroy instance and clean up
//    */
//   destroy() {
//     this.removeDisclaimer();
//     this.clearAllCache();
//     console.log('CA Prop 65: Destroyed');
//   }
// }

// // Auto-initialize for Horizon theme
// const caProp65Warning = new CaliforniaProp65Warning({
//   cartDrawerSelector: 'cart-drawer-component',
//   cartDrawerDialogSelector: '.cart-drawer__dialog',
//   cartDrawerInnerSelector: '.cart-drawer__inner',
//   cartSummarySelector: '.cart-drawer__summary',
//   targetSelector: '.cart__ctas',
//   checkDelay: 500,
//   enableLocationChangeDetection: true // Enable VPN/location change detection
// });

// // Make globally accessible
// window.CaliforniaProp65Warning = CaliforniaProp65Warning;
// window.caProp65Warning = caProp65Warning;

// console.log('🚀 CA Prop 65 Warning: Initialized with VPN detection');



